import React, { Component } from 'react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Checklist } from '../../components/Checklist';
import { INTERNET_SAFETY_CHECKLIST } from '../../utils/constants';
import { Globe, Eye, Wifi, Search, AlertTriangle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
export function Internet() {
  return <Layout data-id="element-4739">
      <div className="container mx-auto px-4 py-12" data-id="element-4740">
        {/* Hero */}
        <div className="mb-12" data-id="element-4741">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 text-blue-500 text-xs font-medium mb-4 border border-blue-500/20" data-id="element-4742">
            <Globe className="h-3 w-3 mr-2" data-id="element-4743" />
            Personal Protocol
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4" data-id="element-4744">
            Internet Safety
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl" data-id="element-4745">
            Navigate the web without leaving a trail or getting infected.
            Protect your browsing habits from surveillance and tracking.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8" data-id="element-4746">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12" data-id="element-4747">
            {/* Why This Matters */}
            <section data-id="element-4748">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-4749">
                Why This Matters
              </h2>
              <div className="bg-slate-900/50 border-l-4 border-blue-500 p-6 rounded-r-lg" data-id="element-4750">
                <p className="text-slate-300 mb-4" data-id="element-4751">
                  Every website you visit, search you make, and link you click
                  is tracked. ISPs, governments, and advertisers build detailed
                  profiles of your life based on your browsing habits.
                </p>
                <ul className="space-y-2 text-slate-400" data-id="element-4752">
                  <li className="flex items-start" data-id="element-4753">
                    <span className="text-blue-500 mr-2" data-id="element-4754">•</span> Public Wi-Fi
                    is inherently insecure and easily intercepted
                  </li>
                  <li className="flex items-start" data-id="element-4755">
                    <span className="text-blue-500 mr-2" data-id="element-4756">•</span> Malicious
                    websites can infect your device just by visiting them
                  </li>
                  <li className="flex items-start" data-id="element-4757">
                    <span className="text-blue-500 mr-2" data-id="element-4758">•</span> DNS queries
                    reveal your browsing history to your ISP
                  </li>
                </ul>
              </div>
            </section>

            {/* Core Components */}
            <section data-id="element-4759">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-4760">
                Core Components
              </h2>
              <div className="grid gap-6" data-id="element-4761">
                <ComponentCard title="Secure DNS" desc="Use encrypted DNS (DNS-over-HTTPS) like Cloudflare (1.1.1.1) or Quad9 to hide queries from your ISP." icon={Globe} data-id="element-4762" />
                <ComponentCard title="VPN Usage" desc="Use a reputable VPN on public Wi-Fi to encrypt your traffic. Avoid free VPNs that sell your data." icon={Wifi} data-id="element-4763" />
                <ComponentCard title="Browser Hardening" desc="Use privacy-focused browsers and extensions like uBlock Origin to block trackers and ads." icon={Eye} data-id="element-4764" />
                <ComponentCard title="Search Engines" desc="Use privacy-focused search engines like DuckDuckGo or Startpage to avoid search tracking." icon={Search} data-id="element-4765" />
              </div>
            </section>

            {/* Implementation Steps */}
            <section data-id="element-4766">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-4767">
                Implementation Steps
              </h2>
              <div className="space-y-4" data-id="element-4768">
                <Step number={1} text="Configure secure DNS (1.1.1.1 or 9.9.9.9) on your devices." data-id="element-4769" />
                <Step number={2} text="Install uBlock Origin browser extension." data-id="element-4770" />
                <Step number={3} text="Disable third-party cookies in your browser settings." data-id="element-4771" />
                <Step number={4} text="Set up a reputable VPN for use on public Wi-Fi." data-id="element-4772" />
                <Step number={5} text="Switch your default search engine to a privacy-focused alternative." data-id="element-4773" />
              </div>
            </section>

            {/* Common Mistakes */}
            <section data-id="element-4774">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-4775">
                Common Mistakes
              </h2>
              <div className="bg-red-500/10 border border-red-500/20 p-6 rounded-lg" data-id="element-4776">
                <ul className="space-y-3 text-slate-300" data-id="element-4777">
                  <li className="flex items-start" data-id="element-4778">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-4779" />
                    Using free VPNs (they often log and sell your data).
                  </li>
                  <li className="flex items-start" data-id="element-4780">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-4781" />
                    Trusting public Wi-Fi without a VPN for sensitive tasks.
                  </li>
                  <li className="flex items-start" data-id="element-4782">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-4783" />
                    Installing too many browser extensions (increases attack
                    surface).
                  </li>
                </ul>
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-4784">
            <Card className="p-6 sticky top-24" data-id="element-4785">
              <h3 className="text-lg font-bold text-slate-100 mb-4" data-id="element-4786">
                Internet Safety Checklist
              </h3>
              <Checklist items={INTERNET_SAFETY_CHECKLIST} id="personal-internet" data-id="element-4787" />

              <div className="mt-8 pt-6 border-t border-slate-800" data-id="element-4788">
                <h4 className="text-sm font-bold text-slate-400 uppercase mb-4" data-id="element-4789">
                  Next Steps
                </h4>
                <Link to="/personal/communication" className="block w-full" data-id="element-4790">
                  <Button className="w-full bg-blue-500 hover:bg-blue-600 text-slate-950" data-id="element-4791">
                    Next: Secure Communication{' '}
                    <ArrowRight className="ml-2 h-4 w-4" data-id="element-4792" />
                  </Button>
                </Link>
                <Link to="/tools/privacy" className="block w-full mt-3" data-id="element-4793">
                  <Button variant="secondary" className="w-full" data-id="element-4794">
                    Privacy Tools
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>;
}
function ComponentCard({
  title,
  desc,
  icon: Icon
}: {
  title: string;
  desc: string;
  icon: any;
}) {
  return <div className="bg-slate-900 p-6 rounded-lg border border-slate-800 flex items-start" data-id="element-4795">
      <div className="bg-blue-500/10 p-3 rounded-lg mr-4 flex-shrink-0" data-id="element-4796">
        <Icon className="h-6 w-6 text-blue-500" data-id="element-4797" />
      </div>
      <div data-id="element-4798">
        <h3 className="text-lg font-bold text-slate-200 mb-2" data-id="element-4799">{title}</h3>
        <p className="text-slate-400 text-sm" data-id="element-4800">{desc}</p>
      </div>
    </div>;
}
function Step({
  number,
  text
}: {
  number: number;
  text: string;
}) {
  return <div className="flex items-center bg-slate-900/50 p-4 rounded border border-slate-800" data-id="element-4801">
      <div className="w-8 h-8 rounded-full bg-slate-800 text-blue-500 font-bold flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-4802">
        {number}
      </div>
      <p className="text-slate-300" data-id="element-4803">{text}</p>
    </div>;
}